/**
 * Theme utility functions
 * =======================
 * Pure helpers that use tokens — no React, no side effects.
 * All functions are fully typed and tested.
 */

import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { colors, type CategoryKey } from "./tokens";

// ─── cn() ────────────────────────────────────────────────────────────────────
/**
 * Merges Tailwind classes safely, handling conflicts.
 * This is the ONLY way to compose class names in this project.
 *
 * @example
 * cn("px-4 py-2", isActive && "bg-primary", className)
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

// ─── getCategoryStyle() ──────────────────────────────────────────────────────
/**
 * Returns the background and text color for a book category badge.
 * Falls back to 'other' if the category is unknown.
 *
 * @example
 * const { bg, text } = getCategoryStyle("fantasy");
 * // { bg: "#EDE9FE", text: "#4C1D95" }
 */
export function getCategoryStyle(category: string | undefined | null): { bg: string; text: string } {
  if (!category) return colors.category.other;
  const key = category.toLowerCase() as CategoryKey;
  return colors.category[key] ?? colors.category.other;
}

// ─── getDistanceBadgeStyle() ─────────────────────────────────────────────────
/**
 * Returns styles for the distance/condition badge on a book card.
 */
export type DistanceBadgeStyle = {
  bg: string;
  text: string;
  label: string;
};

export function getDistanceBadgeStyle(
  distance: number,
  isNew: boolean
): DistanceBadgeStyle {
  if (isNew) {
    return { bg: "#FEF3C7", text: "#92400E", label: "New" };
  }
  if (distance <= 1) {
    return { bg: "#DCFAEF", text: "#065F46", label: `${distance} mi` };
  }
  return { bg: "#EDE9FE", text: "#4C1D95", label: `${distance} mi` };
}

// ─── formatDuration() ────────────────────────────────────────────────────────
/**
 * Formats a lending duration into a human-readable string.
 *
 * @example
 * formatDuration(4, "weeks")  → "4 wks"
 * formatDuration(1, "months") → "1 mo"
 * formatDuration(7, "days")   → "7 days"
 */
export function formatDuration(
  value: number,
  unit: "days" | "weeks" | "months"
): string {
  switch (unit) {
    case "days":   return `${value} day${value !== 1 ? "s" : ""}`;
    case "weeks":  return `${value} wk${value !== 1 ? "s" : ""}`;
    case "months": return `${value} mo`;
  }
}

/**
 * Converts any duration to weeks for storage normalisation.
 */
export function toWeeks(value: number, unit: "days" | "weeks" | "months"): number {
  switch (unit) {
    case "days":   return Math.ceil(value / 7);
    case "weeks":  return value;
    case "months": return value * 4;
  }
}
